import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Switch, Input } from 'antd';

import { isRegionalSupplyHeadRole, isSupplyRole, isAdminRole } from '~helpers/auth';

function TipContentModal({
  t,
  onClose,
  visible,
  setPriceTip,
  priceTip,
  loading,
  mutationHomepageSetting,
}) {
  const originalData = useRef();
  const [cnPriceTipCheck, setCnPriceTipCheck] = useState(priceTip?.cnPriceTip?.enabled);
  const [rowPriceTipCheck, setRowPriceTipCheck] = useState(priceTip?.rowPriceTip?.enabled);
  const [cnInputError, setCnInputError] = useState(false);
  const [rowInputError, setRowInputError] = useState(false);
  const [rowInputValue, setRowInputValue] = useState(priceTip?.rowPriceTip?.tipContent);
  const [cnInputValue, setCnInputValue] = useState(priceTip?.cnPriceTip?.tipContent);
  const [validateResult, setValidateResult] = useState(true);

  const handleConfirmClick = () => {
    if (validateResult) {
      mutationHomepageSetting();
    }
  };

  const handleCNSwitch = value => {
    if (value && cnInputError) {
      setCnInputError(false);
    }

    setCnPriceTipCheck(value);
  };

  const handleRowSwitch = value => {
    if (value && rowInputError) {
      setRowInputError(false);
    }

    setRowPriceTipCheck(value);
  };

  const handleCNInput = e => {
    const inputValue = e.target.value;
    if (inputValue.length > 15) {
      setCnInputError(true);
      setValidateResult(false);
    } else {
      setCnInputValue(inputValue);
      setCnInputError(false);
    }
  };

  const handleRowInput = e => {
    const inputValue = e.target.value;
    if (inputValue.length > 50) {
      setRowInputError(true);
    } else {
      setRowInputValue(inputValue);
      setRowInputError(false);
    }
  };

  const handleOnClose = () => {
    setPriceTip(originalData.current);
    onClose();
  };

  useEffect(() => {
    const cnPriceValidateResult = cnPriceTipCheck || (!cnPriceTipCheck && !cnInputError);
    const rowPriceValidateResult = rowPriceTipCheck || (!rowPriceTipCheck && !rowInputError);
    if (cnPriceValidateResult && rowPriceValidateResult) {
      setPriceTip({
        rowPriceTip: {
          enabled: rowPriceTipCheck,
          tipContent: rowInputValue,
        },
        cnPriceTip: {
          enabled: cnPriceTipCheck,
          tipContent: cnInputValue,
        },
      });
      setValidateResult(true);
    } else {
      setValidateResult(false);
    }
  }, [rowInputError, cnInputError, rowInputValue, cnInputValue, rowPriceTipCheck, cnPriceTipCheck]);

  useEffect(() => {
    if (visible) {
      originalData.current = priceTip;
    }
  }, [visible]);

  return (
    <Modal
      visible={visible}
      onCancel={handleOnClose}
      width={812}
      wrapClassName="tip-content-modal"
      footer={null}
      centered
      confirmLoading={loading}
      maskClosable={false}
    >
      <div className="tip-content-modal__content">
        <h3 className="tip-content-modal__title">
          {t('cms.dashboard.strategy.homepage_setting.tip_content_modal.edit_tip')}
        </h3>
        <div className="operation-wrapper">
          <div className="opera-label">
            <p className="switch-label">
              {t('cms.dashboard.strategy.homepage_setting.tip_content_modal.tip_cn')}
            </p>
            <div className="switch-wrapper">
              <span className="switch-prefix">
                {t('cms.dashboard.strategy.homepage_setting.tip_content_modal.tip_content_display')}
              </span>
              <Switch
                onChange={handleCNSwitch}
                checkedChildren="ON"
                unCheckedChildren="OFF"
                defaultChecked={cnPriceTipCheck}
              />
            </div>
          </div>
          <div className="input-wrapper">
            <p className="tip-content-label">
              {t('cms.dashboard.strategy.homepage_setting.tip_content_modal.tip_content_cn')}
            </p>
            <div className="input-container">
              <Input
                value={cnInputValue}
                onChange={handleCNInput}
                placeholder="Input tip content"
                className={cnInputError ? 'tip-content-input error' : 'tip-content-input'}
                defaultValue={priceTip?.cnPriceTip?.tipContent}
              />
              {cnInputError && (
                <div className="error-tip">
                  {t('cms.landlord.token.create_modal.char_limit', { count: 15 })}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="operation-wrapper">
          <div className="opera-label">
            <p className="switch-label">
              {t('cms.dashboard.strategy.homepage_setting.tip_content_modal.tip_row')}
            </p>
            <div className="switch-wrapper">
              <span className="switch-prefix">
                {t('cms.dashboard.strategy.homepage_setting.tip_content_modal.tip_content_display')}
              </span>
              <Switch
                onChange={handleRowSwitch}
                checkedChildren="ON"
                unCheckedChildren="OFF"
                defaultChecked={rowPriceTipCheck}
              />
            </div>
          </div>
          <div className="input-wrapper">
            <p className="tip-content-label">
              {t('cms.dashboard.strategy.homepage_setting.tip_content_modal.tip_content_row')}
            </p>
            <div className="input-container">
              <Input
                value={rowInputValue}
                onChange={handleRowInput}
                placeholder="Input tip content"
                defaultValue={priceTip?.rowPriceTip?.tipContent}
                className={rowInputError ? 'tip-content-input error' : 'tip-content-input'}
              />
              {rowInputError && (
                <div className="error-tip">
                  {t('cms.landlord.token.create_modal.char_limit', { count: 50 })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <Button onClick={handleOnClose} className="cancel-btn">
          {t('cms:cms.tag_modal.header.btn.cancel')}
        </Button>
        <If condition={isAdminRole() || isSupplyRole() || isRegionalSupplyHeadRole()}>
          <Button
            type="primary"
            disabled={!validateResult}
            onClick={handleConfirmClick}
            className="confirm-btn"
          >
            {t('cms:cms.tag_modal.header.btn.confirm')}
          </Button>
        </If>
      </div>
    </Modal>
  );
}

TipContentModal.propTypes = {
  t: PropTypes.func,
  onClose: PropTypes.func,
  visible: PropTypes.bool,
};

TipContentModal.defaultProps = {
  t: () => {},
  onClose: () => {},
  visible: false,
};

export default TipContentModal;
